/**
 * reels-product.js
 * Handles: card click → popup, video play/pause/mute,
 *          real-time Firebase view/like/comment counts,
 *          add-to-cart (Shopify AJAX API), comments,
 *          share, name modal, toast.
 *
 * Loaded with `defer` — safe to use DOMContentLoaded or
 * direct execution (DOM is ready by then).
 */

(function () {
  'use strict';

  /* ─── Constants ─────────────────────────────────────────── */
  const LS_NAME_KEY  = 'rp_username';
  const LS_LIKED_KEY = 'rp_liked_'; // + sectionId + '_' + idx

  /* ─── Utility helpers ────────────────────────────────────── */

  /** Format a raw number into a short string: 1200 → "1.2K" */
  function fmtCount(n) {
    n = parseInt(n, 10) || 0;
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000)    return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  }

  /** Show a brief toast message inside a given section */
  function showToast(sid, msg) {
    const el = document.getElementById('rp-toast-' + sid);
    if (!el) return;
    el.textContent = msg;
    el.classList.add('rp-show');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('rp-show'), 2400);
  }

  /** Safely parse JSON from localStorage */
  function lsGet(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  }
  function lsSet(key, val) {
    try { localStorage.setItem(key, val); } catch {}
  }

  /** Random hex color for comment avatars */
  function randColor() {
    const colors = ['#e040fb','#ef5350','#42a5f5','#66bb6a','#ffa726','#26c6da','#ff7043'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  /** Debounce – prevents Firebase spam on rapid clicks */
  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  /* ─── Add to Cart (Shopify AJAX API) ─────────────────────── */
  async function addToCart(variantId, btn) {
    if (!variantId) return;
    const orig = btn.textContent;
    btn.disabled   = true;
    btn.textContent = '…';
    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error('cart error');
      btn.textContent = '✓ Added!';
      // Refresh cart bubble if theme supports it
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch {
      btn.textContent = 'Error – Retry';
    } finally {
      setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 1800);
    }
  }

  /* ─── Firebase helpers ───────────────────────────────────── */

  /** Wait for Firebase SDK to be ready (loaded asynchronously) */
  function onFirebaseReady(cb) {
    if (window.__firebaseReady) { cb(); return; }
    document.addEventListener('firebaseReady', cb, { once: true });
  }

  /**
   * Real-time view counter.
   * - Increments view count once per session (sessionStorage flag).
   * - Animates the badge count in when first value arrives.
   */
  function initViewCounter(db, sectionId, blockIdx) {
    const path    = `views/${sectionId}/${blockIdx}`;
    const ref     = db.ref(path);
    const el      = document.getElementById(`rp-vc-${sectionId}-${blockIdx}`);
    if (!el) return;

    const sessionKey = `rp_viewed_${sectionId}_${blockIdx}`;
    const alreadySeen = sessionStorage.getItem(sessionKey);

    // Increment once per session
    if (!alreadySeen) {
      sessionStorage.setItem(sessionKey, '1');
      ref.transaction(cur => (cur || 0) + 1);
    }

    // Real-time listener
    ref.on('value', snap => {
      const val = snap.val() || 0;
      el.textContent = fmtCount(val);
      el.classList.add('rp-visible');
    });
  }

  /**
   * Real-time like toggle.
   * Stores like state in localStorage (guest-friendly, no auth needed).
   */
  function initLikeButton(db, sectionId, blockIdx, likeBtn, cntEl) {
    const path    = `likes/${sectionId}/${blockIdx}`;
    const ref     = db.ref(path);
    const lsKey   = `${LS_LIKED_KEY}${sectionId}_${blockIdx}`;
    let   liked   = lsGet(lsKey) === '1';

    if (liked) likeBtn.classList.add('rp-liked');

    // Real-time count
    ref.on('value', snap => {
      cntEl.textContent = fmtCount(snap.val() || 0);
    });

    const toggle = debounce(() => {
      liked = !liked;
      lsSet(lsKey, liked ? '1' : '0');
      likeBtn.classList.toggle('rp-liked', liked);
      ref.transaction(cur => Math.max(0, (cur || 0) + (liked ? 1 : -1)));
    }, 300);

    likeBtn.addEventListener('click', toggle);
    likeBtn.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') toggle(); });
  }

  /**
   * Real-time comment count badge (number in action bar).
   */
  function initCommentCount(db, sectionId, blockIdx, cntEl) {
    const ref = db.ref(`comments/${sectionId}/${blockIdx}`);
    ref.on('value', snap => {
      const n = snap.exists() ? snap.numChildren() : 0;
      cntEl.textContent = fmtCount(n);
    });
  }

  /**
   * Load and render comments in the comment panel.
   * Listens in real-time so new comments appear without refresh.
   */
  function listenComments(db, sectionId, blockIdx, listEl, isAdmin) {
    const ref = db.ref(`comments/${sectionId}/${blockIdx}`).orderByChild('ts').limitToLast(60);

    listEl.innerHTML = '';

    ref.on('child_added', snap => {
      const c   = snap.val();
      const key = snap.key;
      if (!c || !c.text) return;

      const item   = document.createElement('div');
      item.className = 'rp-comment-item';
      item.dataset.key = key;

      const color   = c.color || randColor();
      const initial = (c.user || '?')[0].toUpperCase();
      const timeAgo = _timeAgo(c.ts);

      const adminBadge = c.isAdmin
        ? `<span class="rp-admin-badge">⭐ Admin</span> `
        : '';

      const deleteBtn = isAdmin
        ? `<span class="rp-comment-del" data-key="${key}"
               style="float:right;cursor:pointer;color:#555;font-size:12px;margin-left:4px"
               role="button" tabindex="0" aria-label="Delete comment">🗑</span>`
        : '';

      item.innerHTML = `
        <div class="rp-comment-avatar" style="background:${color}">${initial}</div>
        <div class="rp-comment-body">
          <div class="rp-comment-user" style="color:var(--rp-accent,#FF3D7F)">
            ${adminBadge}${_esc(c.user || 'Guest')}${deleteBtn}
          </div>
          <div class="rp-comment-text">${_esc(c.text)}</div>
          <div class="rp-comment-time">${timeAgo}</div>
        </div>`;

      // Delete handler (admin only)
      const del = item.querySelector('.rp-comment-del');
      if (del) {
        del.addEventListener('click', () => {
          db.ref(`comments/${sectionId}/${blockIdx}/${key}`).remove();
        });
      }

      listEl.appendChild(item);
      listEl.scrollTop = listEl.scrollHeight;
    });

    ref.on('child_removed', snap => {
      const el = listEl.querySelector(`[data-key="${snap.key}"]`);
      if (el) el.remove();
    });

    return ref; // caller can call ref.off() on close
  }

  /** Post a new comment to Firebase */
  async function postComment(db, sectionId, blockIdx, text, userName, isAdmin) {
    if (!text.trim()) return;
    const ref = db.ref(`comments/${sectionId}/${blockIdx}`);
    await ref.push({
      user:    userName || 'Guest',
      text:    text.trim().slice(0, 200),
      ts:      firebase.database.ServerValue.TIMESTAMP,
      color:   randColor(),
      isAdmin: !!isAdmin
    });
  }

  /* ─── Internal helpers ───────────────────────────────────── */
  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function _timeAgo(ts) {
    if (!ts) return '';
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 60)   return 'just now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
    return Math.floor(diff / 86400) + 'd ago';
  }

  /* ─── Name modal ─────────────────────────────────────────── */
  function getUserName(sectionId) {
    return new Promise(resolve => {
      const stored = lsGet(LS_NAME_KEY);
      if (stored) { resolve(stored); return; }

      // Build modal dynamically (avoids extra DOM in liquid)
      const overlay = document.createElement('div');
      overlay.className = 'rp-name-overlay';
      overlay.innerHTML = `
        <div class="rp-name-card" id="_rpnc">
          <div class="rp-name-header" style="background:linear-gradient(135deg,var(--rp-accent,#FF3D7F),#ff8c42)">
            <div class="rp-name-avatar">👤</div>
            <div class="rp-name-heading">What's your name?</div>
            <div class="rp-name-sub">So others know who you are</div>
          </div>
          <div class="rp-name-body">
            <div class="rp-name-input-wrap">
              <span class="rp-name-icon">✏️</span>
              <input class="rp-name-input" id="_rpni" type="text"
                     placeholder="Your name or nickname" maxlength="30"
                     autocomplete="name" autofocus>
            </div>
            <div class="rp-name-error" id="_rpne" style="color:var(--rp-accent,#FF3D7F)">
              Please enter at least 2 characters.
            </div>
            <button class="rp-name-save" id="_rpns"
                    style="background:linear-gradient(135deg,var(--rp-accent,#FF3D7F),#ff6eb0)">
              Save &amp; Continue
            </button>
            <span class="rp-name-skip" id="_rpnsk">Skip for now</span>
          </div>
        </div>`;

      document.body.appendChild(overlay);

      // Animate in
      requestAnimationFrame(() => {
        overlay.style.background = 'rgba(0,0,0,0.6)';
        document.getElementById('_rpnc').style.transform = 'translateY(0)';
      });

      function close(name) {
        overlay.style.background = 'rgba(0,0,0,0)';
        document.getElementById('_rpnc').style.transform = 'translateY(100%)';
        setTimeout(() => overlay.remove(), 400);
        resolve(name || 'Guest');
      }

      document.getElementById('_rpns').addEventListener('click', () => {
        const val = document.getElementById('_rpni').value.trim();
        if (val.length < 2) {
          document.getElementById('_rpne').style.display = 'block';
          return;
        }
        lsSet(LS_NAME_KEY, val);
        close(val);
      });

      document.getElementById('_rpnsk').addEventListener('click', () => close('Guest'));

      document.getElementById('_rpni').addEventListener('keydown', e => {
        if (e.key === 'Enter') document.getElementById('_rpns').click();
      });
    });
  }

  /* ─── Section initialiser ────────────────────────────────── */
  function initSection(sectionId) {
    const cards    = document.querySelectorAll(`.rp-card-${sectionId}`);
    const modal    = document.getElementById(`rp-modal-${sectionId}`);
    const popup    = document.getElementById(`rp-popup-vid-${sectionId}`);
    const closeBtn = document.getElementById(`rp-close-${sectionId}`);
    const ppOver   = document.getElementById(`rp-pp-${sectionId}`);
    const ppIcon   = document.getElementById(`rp-pp-icon-${sectionId}`);
    const volBtn   = document.getElementById(`rp-vol-${sectionId}`);
    const volOn    = document.getElementById(`rp-vol-on-${sectionId}`);
    const volOff   = document.getElementById(`rp-vol-off-${sectionId}`);
    const unmute   = document.getElementById(`rp-unmute-${sectionId}`);
    const likeBtn  = document.getElementById(`rp-like-${sectionId}`);
    const likeCnt  = document.getElementById(`rp-like-cnt-${sectionId}`);
    const cmtBtn   = document.getElementById(`rp-cmt-btn-${sectionId}`);
    const cmtCnt   = document.getElementById(`rp-cmt-cnt-${sectionId}`);
    const cmtPanel = document.getElementById(`rp-cmt-panel-${sectionId}`);
    const cmtClose = document.getElementById(`rp-cmt-close-${sectionId}`);
    const cmtList  = document.getElementById(`rp-cmt-list-${sectionId}`);
    const cmtInput = document.getElementById(`rp-cmt-input-${sectionId}`);
    const cmtSend  = document.getElementById(`rp-cmt-send-${sectionId}`);
    const shareBtn = document.getElementById(`rp-share-${sectionId}`);
    const barBtn   = document.getElementById(`rp-bar-btn-${sectionId}`);
    const barImg   = document.getElementById(`rp-bar-img-${sectionId}`);
    const barName  = document.getElementById(`rp-bar-name-${sectionId}`);
    const barPrice = document.getElementById(`rp-bar-price-${sectionId}`);
    const leftVid  = modal ? modal.querySelector('.rp-side-vid.rp-left') : null;
    const rightVid = modal ? modal.querySelector('.rp-side-vid.rp-right') : null;

    if (!modal || !popup) return;

    let currentIdx     = 0;
    let currentVid     = null; // block-level video element
    let cmtDbRef       = null; // active Firebase listener
    let currentVarId   = null;
    const isAdmin      = window.__rpIsAdmin || false;

    /* ── Card inline video: autoplay muted on hover ─────────── */
    cards.forEach(card => {
      const vid = card.querySelector('video');
      if (!vid) return;
      card.addEventListener('mouseenter', () => {
        vid.muted = true;
        vid.play().catch(() => {});
      });
      card.addEventListener('mouseleave', () => {
        vid.pause();
        vid.currentTime = 0;
      });
    });

    /* ── Card click → open popup ─────────────────────────────── */
    cards.forEach(card => {
      card.addEventListener('click', () => openModal(parseInt(card.dataset.idx, 10)));
    });

    /* ── Add-to-cart buttons on cards ─────────────────────────── */
    document.querySelectorAll(`.rp-card-${sectionId} .rp-atc-btn`).forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        addToCart(btn.dataset.vid, btn);
      });
    });

    /* ── Open popup ──────────────────────────────────────────── */
    function openModal(idx) {
      const card = cards[idx];
      if (!card) return;

      currentIdx   = idx;
      currentVarId = card.dataset.vid || null;

      // Pause card inline video
      currentVid = card.querySelector('video');
      if (currentVid) currentVid.pause();

      // Fill bottom bar
      barImg.src        = card.dataset.img   || '';
      barImg.alt        = card.dataset.title || '';
      barName.textContent  = card.dataset.title || '—';
      barPrice.textContent = card.dataset.price  || '—';

      // Set popup video src
      const srcVid = card.querySelector('video');
      const srcImg = card.querySelector('img.rp-poster');
      if (srcVid && srcVid.src) {
        popup.src = srcVid.src;
      } else if (srcVid && srcVid.querySelector('source')) {
        popup.src = srcVid.querySelector('source').src;
      } else {
        popup.src = '';
      }

      // Side 3D previews
      const prevCard = cards[idx - 1];
      const nextCard = cards[idx + 1];
      if (leftVid)  leftVid.src  = prevCard ? (_getVidSrc(prevCard) || '') : '';
      if (rightVid) rightVid.src = nextCard ? (_getVidSrc(nextCard) || '') : '';

      // Show modal
      modal.classList.add('rp-open');
      document.body.style.overflow = 'hidden';

      // Play popup video
      popup.muted = true;
      popup.currentTime = 0;
      popup.play().then(() => {
        volBtn.style.display = 'flex';
        _updateVolUI();
        // Show unmute banner for 3 seconds
        unmute.style.display = 'flex';
        setTimeout(() => { unmute.style.display = 'none'; }, 3000);
      }).catch(() => {});

      // Firebase: likes, comments, views
      onFirebaseReady(() => {
        const db = firebase.database();
        initLikeButton(db, sectionId, idx, likeBtn, likeCnt);
        initCommentCount(db, sectionId, idx, cmtCnt);
        initViewCounter(db, sectionId, idx);
      });
    }

    /* ── Close popup ─────────────────────────────────────────── */
    function closeModal() {
      modal.classList.remove('rp-open');
      popup.pause();
      popup.src = '';
      if (leftVid)  { leftVid.pause();  leftVid.src  = ''; }
      if (rightVid) { rightVid.pause(); rightVid.src = ''; }
      document.body.style.overflow = '';
      cmtPanel.classList.remove('rp-open');
      if (cmtDbRef) { cmtDbRef.off(); cmtDbRef = null; }
      // Resume card video if any
      if (currentVid) currentVid.play().catch(() => {});
    }

    closeBtn.addEventListener('click',  closeModal);
    closeBtn.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') closeModal(); });
    modal.addEventListener('click', e => { if (e.target === modal) closeModal(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.classList.contains('rp-open')) closeModal();
    });

    /* ── Play / Pause ────────────────────────────────────────── */
    ppOver.addEventListener('click', () => {
      if (popup.paused) {
        popup.play();
        ppIcon.style.display = 'none';
      } else {
        popup.pause();
        ppIcon.style.display = 'flex';
      }
    });

    /* ── Volume toggle ───────────────────────────────────────── */
    function _updateVolUI() {
      volOn.style.display  = popup.muted ? 'none'  : 'block';
      volOff.style.display = popup.muted ? 'block' : 'none';
    }

    function toggleMute() {
      popup.muted = !popup.muted;
      _updateVolUI();
    }

    volBtn.addEventListener('click',   toggleMute);
    volBtn.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') toggleMute(); });
    unmute.addEventListener('click',  () => { popup.muted = false; _updateVolUI(); unmute.style.display = 'none'; });
    unmute.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        popup.muted = false; _updateVolUI(); unmute.style.display = 'none';
      }
    });

    /* ── Share ───────────────────────────────────────────────── */
    shareBtn.addEventListener('click', () => {
      const url = window.location.href;
      if (navigator.share) {
        navigator.share({ url, title: document.title }).catch(() => {});
      } else {
        navigator.clipboard.writeText(url)
          .then(() => showToast(sectionId, '🔗 Link copied!'))
          .catch(() => showToast(sectionId, 'Copy failed'));
      }
    });

    /* ── Bottom bar ATC ──────────────────────────────────────── */
    barBtn.addEventListener('click', () => addToCart(currentVarId, barBtn));

    /* ── Comments ────────────────────────────────────────────── */
    function openComments() {
      cmtPanel.classList.add('rp-open');
      cmtList.innerHTML = '';
      onFirebaseReady(() => {
        const db = firebase.database();
        if (cmtDbRef) cmtDbRef.off();
        cmtDbRef = listenComments(db, sectionId, currentIdx, cmtList, isAdmin);
      });
    }

    cmtBtn.addEventListener('click',   openComments);
    cmtClose.addEventListener('click',  () => cmtPanel.classList.remove('rp-open'));
    cmtClose.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') cmtPanel.classList.remove('rp-open');
    });

    async function sendComment() {
      const text = cmtInput.value.trim();
      if (!text) return;
      const name = await getUserName(sectionId);
      cmtInput.value = '';
      onFirebaseReady(() => {
        const db = firebase.database();
        postComment(db, sectionId, currentIdx, text, name, isAdmin);
      });
    }

    cmtSend.addEventListener('click', sendComment);
    cmtInput.addEventListener('keydown', e => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(); }
    });

    /* ── Initialise view counters for visible cards ──────────── */
    // Defer until Firebase is ready; use IntersectionObserver
    // so we only start counting a card when it's actually visible.
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const idx = parseInt(entry.target.dataset.idx, 10);
        onFirebaseReady(() => {
          const db = firebase.database();
          initViewCounter(db, sectionId, idx);
        });
        io.unobserve(entry.target); // count only once per page load
      });
    }, { threshold: 0.3 });

    cards.forEach(card => io.observe(card));
  }

  /* ─── Private: get src from a card's video ───────────────── */
  function _getVidSrc(card) {
    const v = card.querySelector('video');
    if (!v) return null;
    if (v.src) return v.src;
    const s = v.querySelector('source');
    return s ? s.src : null;
  }

  /* ─── Boot: initialise every section on the page ─────────── */
  function boot() {
    (window.RP_SECTIONS || []).forEach(sid => {
      try { initSection(sid); }
      catch (e) { console.warn('[ReelsProduct] init error for', sid, e); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
